import {
  ApolloClient, ApolloLink, from, HttpLink, InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '../app/hooks';
import useAuthentication from './useAuthentication';

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_SERVER_URL}/graphql`,
});

// Keep this outside of the hook.
const cache = new InMemoryCache();

function authMiddleware(authToken: string | null) {
  return new ApolloLink((operation, forward) => {
    if (authToken !== null) {
      operation.setContext({
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    }

    return forward(operation);
  });
}

function useAuthenticatedApolloClient() {
  const loggedIn = useAppSelector((state) => state.auth.isAuthenticated);
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const { logOut } = useAuthentication();

  // The InMemoryCache lives for the whole app lifetime, and log out / log in is
  // a client-side navigation (no page reload), so the cache survives an account
  // switch. Many queries — the fleet dashboard especially — key only on their
  // arguments, not on the logged-in user, so with the default cache-first
  // policy one account would be served another account's cached results. Reset
  // the cache whenever the account changes so every account only ever sees its
  // own data.
  const activeUserId = loggedIn ? currentUser?.id ?? null : null;
  const previousUserId = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (
      previousUserId.current !== undefined
      && previousUserId.current !== activeUserId
    ) {
      cache.reset();
    }
    previousUserId.current = activeUserId;
  }, [activeUserId]);

  // Read by the error link at the time of the error, so the client below does
  // not have to be rebuilt for them.
  const session = useRef({ loggedIn, logOut });
  session.current = { loggedIn, logOut };

  // One client per login, not one per render. This hook re-renders on every
  // route change (useAuthentication reads the location), and every new client
  // made each mounted useQuery start over - a `no-cache` query such as the
  // step's coverage check went out again when the hand-off stripped its
  // params (QA 2026-09-25 #52: GetPostalWithAddress 2x, getBookingPricingConfig
  // 3x). A login, logout or account switch changes the token and still gets a
  // fresh client, exactly as before.
  const token = loggedIn ? currentUser.token : null;
  return useMemo(() => {
    const logoutLink = onError(({ graphQLErrors }) => {
      if (graphQLErrors && graphQLErrors.length) {
        const unauthenticated = graphQLErrors.find(((err) => (
          err?.extensions?.response?.statusCode === 401)));

        if (unauthenticated && session.current.loggedIn) {
          session.current.logOut();
        }
      }
    });

    return new ApolloClient({
      link: from([
        authMiddleware(token),
        logoutLink,
        httpLink,
      ]),
      cache,
    });
  }, [token]);
}

export default useAuthenticatedApolloClient;
